import { ALL_CAPABILITIES, capabilitiesFor, hasCapability } from "./capabilities";
/** Public key nhúng — key vận hành (rotate 2026-10-17 bằng `scripts/issue-license.js --gen-keypair`; private key giữ NGOÀI repo). */
const EMBEDDED_PUBLIC_KEY = {
    kty: "EC",
    crv: "P-256",
    x: "vQ7GchGCTc6S_oazj1LBJXc2ze4WPBGRYOd5v9-6rwQ",
    y: "TRJtHonTKetuURVfo1pRTuDYOQjxlrxjvgdY-cCpT1A",
};
/** Public key test E2E (tạo 2026-10-31 cho licensed E2E staging; private key giữ NGOÀI repo). Chỉ thêm, không thay. */
const EMBEDDED_PUBLIC_KEY_E2E = {
    kty: "EC",
    crv: "P-256",
    x: "0lhsGtQEXXomwfI-XSViNwIKRX8sWTdwZ82qM7GFvR4",
    y: "u6X5-5Ilr0gAAtM2HKEfvjO60OXZVTxxNfh9fkLyIMU",
};
let activePublicKeys = [EMBEDDED_PUBLIC_KEY, EMBEDDED_PUBLIC_KEY_E2E];
function sameKey(a, b) {
    // CS-4 audit: kid participates in identity. Without it, re-registering the
    // same key material under a new kid is silently dropped by addLicensePublicKey,
    // and a payload naming the new kid then finds zero candidates and fails closed
    // on a key the customer paid for — a latent false reject through the
    // `candidates.length === 0 → null` path.
    return a.kty === b.kty && a.crv === b.crv && a.x === b.x && a.y === b.y
        && (a.kid ?? null) === (b.kid ?? null);
}
/** Đổi public key runtime (rotation / test với keypair ephemeral). Giữ API cũ: thay toàn bộ danh sách. */
export function setLicensePublicKey(jwk) {
    activePublicKeys = [jwk];
}
/** Thêm key song song (rotation không downtime: key cũ + mới cùng có hiệu lực). */
export function addLicensePublicKey(jwk) {
    if (!activePublicKeys.some((k) => sameKey(k, jwk)))
        activePublicKeys.push(jwk);
}
/** Đọc danh sách đang active (debug/kiểm tra rotation). */
export function getLicensePublicKeys() {
    return [...activePublicKeys];
}
export function resetLicensePublicKey() {
    activePublicKeys = [EMBEDDED_PUBLIC_KEY, EMBEDDED_PUBLIC_KEY_E2E];
}
const LICENSE_TIERS = ["pro", "enterprise"];
/** G2-T1 — product_code của repo này. Key có `p` khác hằng này bị từ chối. */
export const LICENSE_PRODUCT = "vninvestcharts";
/** Tên capability có thật trong `capabilities.ts`. Dùng để lọc `caps` của license. */
export function isKnownCapability(name) {
    return ALL_CAPABILITIES.includes(name);
}
/**
 * W0.6 — capability THỰC SỰ mà license cấp: `MIN_TIER(tier)` **GIAO** `caps`.
 *
 * Quy tắc bất biến: kết quả **luôn là tập con** của `capabilitiesFor(tier)`.
 * License không bao giờ nâng quyền. Đây là ranh giới an toàn duy nhất giữa
 * "license ký số" và "bán thêm tính năng".
 */
export function licenseCapabilities(license) {
    const byTier = capabilitiesFor(license.tier);
    const allow = license.capabilities;
    if (!allow || allow.length === 0)
        return byTier;
    const allowSet = new Set(allow);
    return byTier.filter((c) => allowSet.has(c));
}
/** `true` khi license cấp capability này (tier đủ VÀ không bị `caps` loại bỏ). */
export function licenseHasCapability(license, capability) {
    if (!hasCapability(license.tier, capability))
        return false;
    const allow = license.capabilities;
    if (!allow || allow.length === 0)
        return true;
    return allow.includes(capability);
}
/** Giao `MIN_TIER(tier)` với danh sách `caps` (dùng nội bộ lúc verify). */
function intersectCapabilities(tier, caps) {
    const byTier = capabilitiesFor(tier);
    if (caps.length === 0)
        return byTier;
    const allowSet = new Set(caps);
    return byTier.filter((c) => allowSet.has(c));
}
function b64urlToBytes(input) {
    try {
        const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
        const binary = globalThis.atob(padded);
        const bytes = new Uint8Array(new ArrayBuffer(binary.length));
        for (let i = 0; i < binary.length; i++)
            bytes[i] = binary.charCodeAt(i);
        return bytes;
    }
    catch {
        return null;
    }
}
/** Parse cấu trúc key (chưa verify crypto). Sai format → null. */
export function parseLicenseKey(key) {
    try {
        if (typeof key !== "string")
            return null;
        const parts = key.trim().split(".");
        if (parts.length !== 3 || parts[0] !== "vni1")
            return null;
        const payloadBytes = b64urlToBytes(parts[1]);
        const sigBytes = b64urlToBytes(parts[2]);
        if (!payloadBytes || !sigBytes || sigBytes.length === 0)
            return null;
        const payload = JSON.parse(new TextDecoder().decode(payloadBytes));
        if (typeof payload.t !== "string" || typeof payload.e !== "number")
            return null;
        const domains = Array.isArray(payload.d) ? payload.d.filter((x) => typeof x === "string") : [];
        // CS-4: `k` is the signing-key id. Must be a non-empty string to count;
        // anything else behaves as absent (try-all), never as a match.
        const kid = typeof payload.k === "string" && payload.k.length > 0 ? payload.k : undefined;
        // CS-5: identity fields. Same rule — non-empty strings or absent. `i` must be
        // a finite number (issued-at epoch seconds) or it is dropped, never trusted.
        // None of these influence the tier decision; they are attribution only.
        const str = (v) => typeof v === "string" && v.length > 0 ? v : undefined;
        const iat = typeof payload.i === "number" && Number.isFinite(payload.i) ? payload.i : undefined;
        // W0.6: `caps` — chỉ giữ tên capability hợp lệ. Tên lạ bị loại (không tin
        // cậy, chống payload phình). Mảng rỗng / không phải mảng ⇒ coi như không có.
        const caps = Array.isArray(payload.caps)
            ? payload.caps.filter((x) => typeof x === "string" && isKnownCapability(x))
            : [];
        // G2-T1: `p` — non-empty string mới tính; còn lại coi như không có
        // (lenient: key legacy không `p` vẫn verify; strict DEFER).
        const product = typeof payload.p === "string" && payload.p.length > 0 ? payload.p : undefined;
        return {
            payloadBytes,
            payload: {
                t: payload.t, e: payload.e, d: domains, k: kid,
                s: str(payload.s), c: str(payload.c), n: str(payload.n), i: iat,
                ...(caps.length > 0 ? { caps } : {}),
                ...(product !== undefined ? { p: product } : {}),
            },
            sigBytes,
        };
    }
    catch {
        return null;
    }
}
/**
 * Interop DER <-> P1363: browser SubtleCrypto (ECDSA) dùng DER (SEQ{INT r, INT s}),
 * còn Node-subtle (test/dev) dùng P1363 raw r||s 64 bytes. Verifier thử cả hai.
 */
function derToP1363(der) {
    try {
        if (der.length < 8 || der[0] !== 0x30)
            return null;
        let o = 2;
        if (der[1] & 0x80) {
            const n = der[1] & 0x7f;
            if (n !== 1 && n !== 2)
                return null;
            o = 2 + n;
        }
        if (der[o] !== 0x02)
            return null;
        const rLen = der[o + 1];
        const r = der.slice(o + 2, o + 2 + rLen);
        o = o + 2 + rLen;
        if (der[o] !== 0x02)
            return null;
        const sLen = der[o + 1];
        const s = der.slice(o + 2, o + 2 + sLen);
        const pad = (v) => {
            let start = 0;
            while (start < v.length - 1 && v[start] === 0)
                start++;
            const body = v.slice(start);
            if (body.length > 32)
                return new Uint8Array(0);
            const out = new Uint8Array(new ArrayBuffer(32));
            out.set(body, 32 - body.length);
            return out;
        };
        const pr = pad(r);
        const ps = pad(s);
        if (pr.length !== 32 || ps.length !== 32)
            return null;
        const out = new Uint8Array(new ArrayBuffer(64));
        out.set(pr, 0);
        out.set(ps, 32);
        return out;
    }
    catch {
        return null;
    }
}
function p1363ToDer(raw) {
    try {
        if (raw.length !== 64)
            return null;
        const encInt = (v) => {
            let start = 0;
            while (start < v.length - 1 && v[start] === 0)
                start++;
            let body = Array.from(v.slice(start));
            if (body[0] !== undefined && body[0] >= 0x80)
                body = [0x00, ...body];
            return [0x02, body.length, ...body];
        };
        const body = [...encInt(raw.slice(0, 32)), ...encInt(raw.slice(32))];
        const len = body.length >= 128 ? [0x81, body.length] : [body.length];
        return new Uint8Array([0x30, ...len, ...body]);
    }
    catch {
        return null;
    }
}
async function tryVerify(subtle, key, sig, data) {
    const alg = { name: "ECDSA", hash: "SHA-256" };
    // CS-4: try EVERY plausible encoding, never pick one by heuristic. The old code
    // tried exactly one alternative chosen by `sig[0] === 0x30`, so a valid P1363
    // signature whose first byte happens to be 0x30 (~1/256 of them) was converted
    // to DER garbage and failed closed on a key the customer paid for. Raw first
    // (it is the canonical issuance going forward), conversions after.
    const candidates = [sig];
    if (sig.length > 0 && sig[0] === 0x30)
        candidates.push(derToP1363(sig));
    if (sig.length === 64)
        candidates.push(p1363ToDer(sig));
    for (const candidate of candidates) {
        if (!candidate)
            continue;
        try {
            if (await subtle.verify(alg, key, candidate, data))
                return true;
        }
        catch {
            // try the next encoding
        }
    }
    return false;
}
function hostAllowed(domains, host) {
    const h = host.toLowerCase();
    return domains.some((d) => {
        const rule = d.toLowerCase();
        if (rule === "*" || rule === h)
            return true;
        if (rule.startsWith("*.") && (h === rule.slice(2) || h.endsWith(rule.slice(1))))
            return true;
        return false;
    });
}
/**
 * Verify chữ ký + hạn + tier + domain. Mọi lỗi → null (fail-closed).
 * `subtle` mặc định lấy từ globalThis (browser/node18+); môi trường thiếu (jsdom) truyền qua opts.
 */
export async function verifyLicenseKey(key, opts = {}) {
    try {
        const parsed = parseLicenseKey(key);
        if (!parsed)
            return null;
        const nowSec = Math.floor((opts.now ?? Date.now()) / 1000);
        if (!Number.isFinite(parsed.payload.e) || parsed.payload.e <= nowSec)
            return null;
        const subtle = opts.subtle ?? globalThis.crypto?.subtle;
        if (!subtle)
            return null;
        // CS-4: kid routing. A payload `k` names its signing key; only keys carrying
        // that id are tried, and an unknown id fails closed with NO fallback to the
        // full list (falling back would let a retired-compromised key verify by
        // accident of list order). No `k` keeps the historical try-all behaviour so
        // every key issued to date still verifies.
        const wantedKid = parsed.payload.k;
        const candidates = wantedKid !== undefined
            ? activePublicKeys.filter((jwk) => jwk.kid === wantedKid)
            : activePublicKeys;
        if (wantedKid !== undefined && candidates.length === 0)
            return null;
        // Rotation: thử từng public key đang active, key nào trúng thì nhận.
        let ok = false;
        for (const jwk of candidates) {
            try {
                const cryptoKey = await subtle.importKey("jwk", { ...jwk, key_ops: ["verify"], ext: true }, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
                if (await tryVerify(subtle, cryptoKey, parsed.sigBytes, parsed.payloadBytes)) {
                    ok = true;
                    break;
                }
            }
            catch {
                // thử key tiếp theo
            }
        }
        if (!ok)
            return null;
        // CS-4: the tier allowlist is checked AFTER the signature, on verified input
        // only. Checking it before (as it was) is a no-op in principle — and in the
        // worst reading it lets an observer distinguish "bad tier" from "bad sig".
        // Fail-closed either way; this order is simply the honest one.
        if (!LICENSE_TIERS.includes(parsed.payload.t))
            return null;
        // G2-T1: product binding — kiểm tra SAU chữ ký (honest order, như tier).
        // `p` absent = key legacy, giữ nguyên. `p` present nhưng sai → null.
        if (parsed.payload.p !== undefined && parsed.payload.p !== LICENSE_PRODUCT)
            return null;
        const domains = parsed.payload.d ?? [];
        if (opts.host && domains.length > 0 && !hostAllowed(domains, opts.host))
            return null;
        return {
            tier: parsed.payload.t,
            exp: parsed.payload.e,
            domains,
            ...(parsed.payload.p !== undefined ? { product: parsed.payload.p } : {}),
            ...(parsed.payload.caps !== undefined
                ? { capabilities: intersectCapabilities(parsed.payload.t, parsed.payload.caps) }
                : {}),
            ...(parsed.payload.s !== undefined ? { serial: parsed.payload.s } : {}),
            ...(parsed.payload.c !== undefined ? { customerCode: parsed.payload.c } : {}),
            ...(parsed.payload.n !== undefined ? { label: parsed.payload.n } : {}),
            ...(parsed.payload.k !== undefined ? { kid: parsed.payload.k } : {}),
        };
    }
    catch {
        return null;
    }
}
